import { Component, ElementRef, HostListener, OnInit, ViewChild } from '@angular/core';
import { MatDialogRef, MatDialog } from '@angular/material/dialog';
import { Product } from '../Module/Product.model';
import { ProductService } from '../service/product.service';
import { OrderService } from '../service/order.service';
import { Order } from '../Module/Order.model';
import { Client } from '../Module/Client.model';
import { ClientService } from '../service/client.service';
import { ReturnOrderComponent } from '../return-order/return-order.component';

@Component({
  selector: 'app-smart-refund-finder',
  templateUrl: './smart-refund-finder.component.html',
  styleUrls: ['./smart-refund-finder.component.css']
})
export class SmartRefundFinderComponent implements OnInit {
  isLoadingProducts = false;
  isLoadingClients = false;
  isSearchingOrders = false;

  products: Product[] = [];
  productsToDisplay: Product[] = [];
  searchTerm: string = '';
  showProductDropdown = false;
  selectedProducts: Product[] = [];

  clients: Client[] = [];
  filteredClients: Client[] = [];
  clientSearchTerm: string = '';
  showClientDropdown = false;
  selectedClient: Client | null = null;
  
  searchStartDate: string = '';
  searchEndDate: string = '';
  searchOrderId?: number;

  foundOrders: Order[] = [];
  hasSearched = false;

  barcodeBuffer = '';
  barcodeTimeout: any;
  readonly BARCODE_TIMEOUT = 50;
  readonly MIN_BARCODE_LENGTH = 3;
  isProcessingBarcode = false;
  userIsTypingInField = false;

  @ViewChild('productSearchInput') productSearchInput!: ElementRef;
  @ViewChild('clientSearchInput') clientSearchInput!: ElementRef;

  constructor(
    private dialogRef: MatDialogRef<SmartRefundFinderComponent>,
    private productService: ProductService,
    private clientService: ClientService,
    private orderService: OrderService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.loadProducts();
    this.loadClients();
  }

  loadProducts(): void {
    this.isLoadingProducts = true;
    this.productService.listProducts().subscribe(data => {
      this.products = data;
      this.productsToDisplay = this.products.slice(0, 10);
      this.isLoadingProducts = false;
    });
  }

  loadClients(): void {
    this.isLoadingClients = true;
    this.clientService.listClients().subscribe(data => {
      this.clients = data;
      this.filteredClients = this.clients.slice(0, 10);
      this.isLoadingClients = false;
    });
  }

  close(): void {
    this.dialogRef.close();
  }

  // --- Product Search ---
  onProductSearchChange(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    this.searchTerm = inputElement.value;
    if (this.searchTerm.length > 0) {
      this.showProductDropdown = true;
      const normalizeString = (str: string) =>
        str.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
      const lowerCaseQuery = normalizeString(this.searchTerm);
      
      this.productsToDisplay = this.products.filter(product => {
        return (product.name && normalizeString(product.name).includes(lowerCaseQuery)) ||
          (product.description && normalizeString(product.description).includes(lowerCaseQuery)) ||
          (product.codeBare && product.codeBare.toString().includes(lowerCaseQuery)) ||
          lowerCaseQuery.split(' ').every(term =>
            (product.name && normalizeString(product.name).includes(term)) ||
            (product.description && normalizeString(product.description).includes(term))
          );
      }).slice(0, 10);
    } else {
      this.productsToDisplay = this.products.slice(0, 10);
    }
  }

  selectProduct(product: Product): void {
    if (!this.selectedProducts.find(p => p.id === product.id)) {
      this.selectedProducts.push(product);
    }
    this.searchTerm = '';
    this.showProductDropdown = false;
  }

  removeProduct(index: number): void {
    this.selectedProducts.splice(index, 1);
  }

  // --- Client Search ---
  onClientSearchChange(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    this.clientSearchTerm = inputElement.value;
    if (this.clientSearchTerm.length > 0) {
      this.showClientDropdown = true;
      const lowerQuery = this.clientSearchTerm.toLowerCase();
      this.filteredClients = this.clients.filter(c => 
        (c.firstName && c.firstName.toLowerCase().includes(lowerQuery)) ||
        (c.lastName && c.lastName.toLowerCase().includes(lowerQuery)) ||
        (c.phoneNumber && c.phoneNumber.includes(lowerQuery))
      ).slice(0, 10);
    } else {
      this.filteredClients = this.clients.slice(0, 10);
    }
  }

  selectClient(client: Client): void {
    this.selectedClient = client;
    this.clientSearchTerm = `${client.firstName} ${client.lastName}`;
    this.showClientDropdown = false;
  }

  removeClient(): void {
    this.selectedClient = null;
    this.clientSearchTerm = '';
  }

  // --- Barcode Reading ---
  @HostListener('document:keypress', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent) {
    const isNumericOrEnter = /^[0-9]$/.test(event.key) || event.key === 'Enter';
    if (!isNumericOrEnter) return;
    if (!this.userIsTypingInField) {
      if (event.key === 'Enter') {
        event.preventDefault();
        this.finalizeBarcodeInput();
      } else {
        this.accumulateBarcode(event.key);
      }
    }
  }

  private accumulateBarcode(key: string): void {
    this.barcodeBuffer += key;
    if (this.barcodeTimeout) clearTimeout(this.barcodeTimeout);
    this.barcodeTimeout = setTimeout(() => this.finalizeBarcodeInput(), this.BARCODE_TIMEOUT);
  }

  private finalizeBarcodeInput(): void {
    if (this.barcodeTimeout) clearTimeout(this.barcodeTimeout);
    if (this.barcodeBuffer.length >= this.MIN_BARCODE_LENGTH && !this.isProcessingBarcode) {
      this.isProcessingBarcode = true;
      this.processBarcode(this.barcodeBuffer.trim());
      this.barcodeBuffer = '';
      setTimeout(() => this.isProcessingBarcode = false, 200);
    } else {
      this.barcodeBuffer = '';
    }
  }

  processBarcode(barcode: string): void {
    const parsedBarcode = barcode.replace(/\D/g, '');
    if (!parsedBarcode || parsedBarcode.length < this.MIN_BARCODE_LENGTH) return;
    
    const product = this.products.find(p => p.codeBare?.toString() === parsedBarcode);
    if (product) {
      this.selectProduct(product);
      // Auto trigger search if product found via scan, only if client is also filled 
      // or if they are just scanning, we shouldn't trigger an error toast if client is missing
      if (this.selectedClient) {
        this.searchOrders();
      }
    }
  }

  @HostListener('document:focus', ['$event'])
  handleFieldFocus(event: FocusEvent) {
    const target = event.target as HTMLElement;
    if (target?.tagName === 'INPUT' || target?.tagName === 'TEXTAREA' || target?.tagName === 'SELECT') {
      this.userIsTypingInField = true;
    }
  }

  @HostListener('document:blur', ['$event'])
  handleFieldBlur(event: FocusEvent) {
    const target = event.target as HTMLElement;
    if (target?.tagName === 'INPUT' || target?.tagName === 'TEXTAREA' || target?.tagName === 'SELECT') {
      setTimeout(() => this.userIsTypingInField = false, 100);
    }
  }

  @HostListener('document:click', ['$event'])
  handleOutsideClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    if (this.productSearchInput && !this.productSearchInput.nativeElement.contains(target) && !target.closest('.d-dropdown')) {
      this.showProductDropdown = false;
    }
    if (this.clientSearchInput && !this.clientSearchInput.nativeElement.contains(target) && !target.closest('.c-dropdown')) {
      this.showClientDropdown = false;
      if (!this.selectedClient) this.clientSearchTerm = '';
    }
  }

  // --- API Search ---
  searchOrders(): void {
    this.isSearchingOrders = true;
    this.hasSearched = true;

    if (this.searchOrderId) {
      this.orderService.getOrderById(this.searchOrderId).subscribe({
        next: (order) => {
          this.foundOrders = order ? [order] : [];
          this.isSearchingOrders = false;
        },
        error: (err) => {
          console.error('Error finding order by ID', err);
          this.foundOrders = [];
          this.isSearchingOrders = false;
        }
      });
      return;
    }

    if (!this.selectedClient) {
      alert("Veuillez renseigner le Client si vous n'avez pas le Numéro de Commande.");
      this.isSearchingOrders = false;
      this.hasSearched = false;
      return;
    }

    if (!this.selectedProducts || this.selectedProducts.length === 0) {
      alert("Veuillez scanner ou sélectionner au moins un Produit Retourné.");
      this.isSearchingOrders = false;
      this.hasSearched = false;
      return;
    }

    const productIds = this.selectedProducts
      .map(p => Number(p.id))
      .filter(id => !isNaN(id));

    if (productIds.length === 0) {
      alert("Erreur: Les produits sélectionnés n'ont pas d'ID valide.");
      this.isSearchingOrders = false;
      this.hasSearched = false;
      return;
    }

    this.orderService.getPagedOrdersByProduct(
      productIds,
      this.selectedClient.id,
      0, 20,
      this.searchStartDate || undefined,
      this.searchEndDate || undefined
    ).subscribe({
      next: (res) => {
        this.foundOrders = res.content || [];
        this.isSearchingOrders = false;
      },
      error: (err) => {
        console.error('Error finding orders by product', err);
        this.isSearchingOrders = false;
      }
    });
  }

  openReturnDialog(order: Order): void {
    const refundDialogRef = this.dialog.open(ReturnOrderComponent, {
      width: '1000px',
      maxWidth: '95vw',
      data: { order: order },
      disableClose: true
    });

    refundDialogRef.afterClosed().subscribe(result => {
      if (result) {
        // Refund was successful, close the finder dialog too
        this.dialogRef.close(true);
      }
    });
  }
}
